const DEFAULT_POST_AUTH_REDIRECT = "/home";
const PENDING_RETURN_TO_STORAGE_KEY = "pending_post_auth_return_to";
const PENDING_RETURN_TO_MAX_AGE_MS = 10 * 60 * 1000;

type PendingReturnToPayload = {
  path: string;
  ts: number;
};

const normalizePath = (path: string): string => {
  if (!path.startsWith("/")) return `/${path}`;
  return path;
};

export const sanitizeReturnToPath = (
  maybePath: string | null | undefined,
): string | null => {
  if (!maybePath) return null;

  const trimmedPath = maybePath.trim();
  if (!trimmedPath) return null;

  if (
    trimmedPath.startsWith("http://") ||
    trimmedPath.startsWith("https://") ||
    trimmedPath.startsWith("//")
  ) {
    return null;
  }

  const normalizedPath = normalizePath(trimmedPath);
  if (normalizedPath.startsWith("/signin")) {
    return null;
  }

  return normalizedPath;
};

export const getPostAuthRedirectPath = (
  maybePath: string | null | undefined,
): string => sanitizeReturnToPath(maybePath) || DEFAULT_POST_AUTH_REDIRECT;

export const buildSigninPathWithReturnTo = (returnToPath: string): string => {
  return `/signin?returnTo=${encodeURIComponent(returnToPath)}`;
};

export const getCurrentRelativeLocation = (): string => {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

export const setPendingPostAuthRedirectPath = (path: string): void => {
  const sanitizedPath = sanitizeReturnToPath(path);
  if (!sanitizedPath) return;

  const payload: PendingReturnToPayload = {
    path: sanitizedPath,
    ts: Date.now(),
  };
  sessionStorage.setItem(
    PENDING_RETURN_TO_STORAGE_KEY,
    JSON.stringify(payload),
  );
};

export const getPendingPostAuthRedirectPath = (): string | null => {
  const rawPayload = sessionStorage.getItem(PENDING_RETURN_TO_STORAGE_KEY);
  if (!rawPayload) return null;

  try {
    const parsedPayload = JSON.parse(rawPayload) as PendingReturnToPayload;
    if (
      !parsedPayload?.path ||
      typeof parsedPayload.ts !== "number" ||
      Date.now() - parsedPayload.ts > PENDING_RETURN_TO_MAX_AGE_MS
    ) {
      sessionStorage.removeItem(PENDING_RETURN_TO_STORAGE_KEY);
      return null;
    }

    return sanitizeReturnToPath(parsedPayload.path);
  } catch {
    sessionStorage.removeItem(PENDING_RETURN_TO_STORAGE_KEY);
    return null;
  }
};

export const clearPendingPostAuthRedirectPath = (): void => {
  sessionStorage.removeItem(PENDING_RETURN_TO_STORAGE_KEY);
};
